import { Dictionary } from '@ngrx/entity';
import { createSelector } from '@ngrx/store';
import {
  selectAllFeaturesEnabled,
  SelectedCustomerState,
  selectOnline,
  UserState,
} from '@panamax/app-state';
import {
  ExceptionResolution,
  Order,
  orderExceptionSelectors,
  OrderStatus,
  partnerSelectors,
} from '@usf/ngrx-order';
import { selectAllSellerPrice } from '@usf/ngrx-pricing';
import { SellerPriceDetails } from '@usf/price-types';
import {
  selectAppStateSelectedCustomer,
  selectAppStateUser,
} from 'src/app/ngrx-customer/store';
import { Product } from 'src/app/shared/models/product.model';
import { productWithAlternativesSelector } from '@shared/selectors/product.selectors';
import { ExceptionType } from '../constants/order-exception-type.enum';
import { getExceptionType } from '../utils/order-exception.util';
import { OrderExceptionViewModel } from '../models/order-exception-view.model';
import { OrderExceptionsHelper } from './order-exceptions.helper';
import { ResolutionTallyService } from '../service/resolution-tally.service';
import { FEATURES } from '@shared/constants/splitio-features';
import { selectUserProfiles } from '@app/user/store/selectors/user.selectors';
import {
  AccessRoleKeyEnum,
  AccessRoleValueEnum,
} from '@shared/constants/order-info-access-role.constant';
import { UserProfile } from '@usf/user-types/user-profile';
import { PartnerState } from '@usf/ngrx-order/lib/models/state/partner-state';
import { PUNCHOUT_PO_PENDING } from '@shared/constants/order-punchout';

export const selectOrderExceptionViewModel = createSelector(
  orderExceptionSelectors.selectOrderExceptionState,
  productWithAlternativesSelector,
  selectOnline(),
  selectAppStateUser,
  selectAllSellerPrice,
  selectAppStateSelectedCustomer,
  selectAllFeaturesEnabled([FEATURES.split_global_national_customers.name]),
  selectUserProfiles,
  partnerSelectors.selectPartner,

  (
    orderExceptionState,
    products,
    online: boolean,
    loggedInUser: UserState,
    sellerPrices: SellerPriceDetails[],
    selectedCustomer: SelectedCustomerState,
    restrictedNationalFlagEnabled: boolean,
    userProfile: UserProfile[],
    partnerState: PartnerState,
  ) => {
    let restrictToOG = false;

    if (restrictedNationalFlagEnabled) {
      restrictToOG =
        selectedCustomer?.restrictToOG === 'Y' ||
        selectedCustomer?.restrictToOG === 'M' ||
        selectedCustomer?.customerType === 'NA';
    }

    const accessRolePair = userProfile?.find(
      pair => pair.key === AccessRoleKeyEnum.ACCESS_ROLE_KEYS,
    );

    let noOrderEnabled = accessRolePair
      ? accessRolePair.value === AccessRoleValueEnum.NO_SUBMIT_VALUE
      : false;

    const exceptions: Map<ExceptionType, ExceptionResolution[]> = new Map();
    const productMap: Map<number, Product> = new Map();
    const searchForSubsMap: Map<number, string> = new Map();
    const expandedSection: Map<ExceptionType, boolean> = new Map();
    Object.values(orderExceptionState.entities).forEach(exception => {
      const type = getExceptionType(
        exception.orderException.errorType,
        exception.orderException.errorCode,
      );
      if (exceptions.has(type)) {
        if (type === ExceptionType.PRICE) {
          if (loggedInUser?.tmUser === 'Y') {
            const updateException =
              OrderExceptionsHelper.updateExceptionWithAscCharge(
                exception,
                sellerPrices,
              );
            exceptions.get(type).push(updateException);
          }
        } else {
          exceptions.get(type).push(exception);
        }
      } else {
        if (type === ExceptionType.PRICE) {
          if (loggedInUser?.tmUser === 'Y') {
            const updateException =
              OrderExceptionsHelper.updateExceptionWithAscCharge(
                exception,
                sellerPrices,
              );

            exceptions.set(type, [updateException]);
          }
        } else {
          exceptions.set(type, [exception]);
        }
      }

      if (
        exception?.orderException?.productNumber &&
        products.has(exception.orderException.productNumber)
      ) {
        productMap.set(
          exception.orderException.productNumber,
          products.get(exception.orderException.productNumber),
        );
        searchForSubsMap.set(
          exception.orderException.productNumber,
          products
            .get(exception.orderException.productNumber)
            ?.summary?.productDescTxtl?.replace(',', '')
            .split(' ', 3)
            .join(' '),
        );
        const resolvedSubProductNumber =
          exception.resolutionOrderItem?.substituteProduct?.productNumber;
        if (
          resolvedSubProductNumber &&
          products.has(resolvedSubProductNumber)
        ) {
          productMap.set(
            resolvedSubProductNumber,
            products.get(resolvedSubProductNumber),
          );
        }
        exception.orderException.subOrReplaceItems?.forEach(item =>
          productMap.set(item.productNumber, products.get(item.productNumber)),
        );
      }
    });

    Object.keys(orderExceptionState.expandedSection || {}).forEach(type => {
      expandedSection.set(
        ExceptionType[type],
        orderExceptionState.expandedSection[type],
      );
    });

    const sellerPrice = {};

    sellerPrices?.forEach(price => {
      sellerPrice[price?.productNumber] = price;
    });

    // Sort the Date Order Exceptions by errorCode allowing us to take the first index as priority
    const dateExceptionResolutions = exceptions.get(ExceptionType.DATE);
    if (!!dateExceptionResolutions?.length) {
      const sortedDateExceptionResolutions = sortDateExceptionByPriority(
        dateExceptionResolutions,
      );
      exceptions.set(ExceptionType.DATE, sortedDateExceptionResolutions);
    }

    let order: Order;
    if (
      partnerState?.partnerId &&
      !orderExceptionState?.order?.orderHeader?.purchaseOrderNumber &&
      (orderExceptionState?.order?.orderHeader?.orderStatus ==
        OrderStatus.SUBMITTED ||
        orderExceptionState?.order?.orderHeader?.orderStatus ==
          OrderStatus.SUBMITTED_WITH_EXCEPTIONS ||
        orderExceptionState?.order?.orderHeader?.orderStatus ==
          OrderStatus.SUBMITTED_CREDIT_HOLD)
    ) {
      order = {
        ...orderExceptionState.order,
        orderHeader: {
          ...orderExceptionState.order.orderHeader,
          purchaseOrderNumber: PUNCHOUT_PO_PENDING,
        },
      };
    } else {
      order = orderExceptionState.order;
    }

    const vm: OrderExceptionViewModel = {
      order,
      orderExceptions: exceptions,
      productMap: productMap,
      searchForSubsMap,
      expandedSection: expandedSection,
      submitEnabled:
        isAtLeastOneExceptionResolved(exceptions) &&
        !restrictToOG &&
        !noOrderEnabled,
      online: online,
      tmUser: loggedInUser?.tmUser === 'Y',
      sellerPrice,
      tallies: ResolutionTallyService.tally(exceptions, order?.orderItems),
      restrictToOg: selectedCustomer?.restrictToOG,
      substitutionsAllowed: selectedCustomer?.substitutionAllowed,
      partnerAutotransfer:
        !!sessionStorage.getItem('autoTransferOrder') &&
        partnerState?.punchoutProfile?.autoTransfer,
      showResolveLater: partnerState?.punchoutProfile?.punchout
        ? false
        : !partnerState?.siteRoleProfile?.siteRestricted &&
          !partnerState?.siteRoleProfile?.exceptionRestricted,
    };

    return vm;
  },
);

export const selectProductNumbersFromException = createSelector(
  orderExceptionSelectors.selectOrderExceptionState,
  orderExceptionState => {
    return getProductNumbers(orderExceptionState.entities);
  },
);

export const selectExceptionResolutionWithId = (entityId: string) =>
  createSelector(
    orderExceptionSelectors.selectOrderExceptionState,
    orderExceptionState => orderExceptionState.entities[entityId],
  );

export const sortDateExceptionByPriority = (
  resolutions: ExceptionResolution[],
): ExceptionResolution[] => {
  if (resolutions) {
    const priorities = [101, 102, 107, 105, 103, 104];
    const sortedResolutions: ExceptionResolution[] = [];
    priorities.forEach(priority => {
      const filteredResolutions = resolutions.filter(
        resolution => resolution.orderException.errorCode === priority,
      );
      if (!!filteredResolutions.length) {
        sortedResolutions.push(...filteredResolutions);
      }
    });
    return sortedResolutions;
  }
};

function getProductNumbers(
  exceptionResolutions: Dictionary<ExceptionResolution>,
): number[] {
  if (!!exceptionResolutions && Object.keys(exceptionResolutions).length) {
    return Object.values(exceptionResolutions)
      .map(exceptionResolution =>
        [exceptionResolution.orderException.productNumber].concat(
          exceptionResolution.orderException.subOrReplaceItems?.map(
            replaceItem => replaceItem.productNumber,
          ),
        ),
      )
      .reduce((previous, current) => previous.concat(current))
      .filter(productNumber => !!productNumber);
  } else {
    return [];
  }
}

function isAtLeastOneExceptionResolved(
  exceptions: Map<ExceptionType, ExceptionResolution[]>,
): boolean {
  let oneExceptionResolved = false;
  for (let exceptionType of exceptions.keys()) {
    if (exceptionType === ExceptionType.DATE) {
      oneExceptionResolved = exceptions.get(exceptionType)[0].dateCompleted;
    } else {
      for (let exceptionResolutions of exceptions.values()) {
        for (let exceptionResolution of exceptionResolutions) {
          if (
            !oneExceptionResolved &&
            exceptionResolution.hasOwnProperty('resolutionOrderItem')
          ) {
            oneExceptionResolved = true;
          }
        }
      }
    }
  }
  return oneExceptionResolved;
}
